import React from 'react';
import { DraftStore, usePendingDrafts } from './builderDrafts';

/** The draft key of a saved plan item: its editor keeps its draft under it, and its deletion clears it. */
export function planDraftKey(kind: 'plan_section' | 'generated_material' | 'vocabulary_target' | 'grammar_target', id: number) {
    return `${kind}:${id}`;
}

/** Lesson plan's Save all, over the page's draft store (builderDrafts.tsx): each editor keeps its draft there with
 * useDraftSave, so a draft whose editor has closed (a dialog closed with its changes) still saves. Save sequentially:
 * every write advances the lesson's authoring revision. ``saveAll`` resolves whether every edit was saved. */
export function usePlanDrafts(store: DraftStore, onDirtyChange?: (dirty: boolean) => void) {
    const pending = usePendingDrafts(store);
    const [saving, setSaving] = React.useState(false);
    const running = React.useRef(false);
    const [message, setMessage] = React.useState<string | null>(null);
    const [failed, setFailed] = React.useState(false);
    // The drafts changed outside a Save all (one saved or cancelled on its own, another begun): its status no longer applies.
    React.useEffect(() => { if (!running.current) { setMessage(null); setFailed(false); } }, [pending]);
    React.useEffect(() => { onDirtyChange?.(saving || pending.length > 0); }, [pending, saving, onDirtyChange]);
    const saveAll = async (): Promise<boolean> => {
        if (running.current) return false;
        const drafts = store.pending();
        const invalid = drafts.find(draft => !draft.valid);
        if (invalid) { setFailed(true); setMessage(`Complete the required fields in ${invalid.label} before saving all.`); return false; }
        running.current = true;
        setSaving(true); setFailed(false); setMessage(null);
        let completed = 0;
        try {
            for (const draft of drafts) { await store.save(draft.key); completed++; }
            setMessage('All plan changes saved.');
            return true;
        } catch {
            setFailed(true);
            setMessage(`${completed ? `${completed} ${completed === 1 ? 'item was' : 'items were'} saved. ` : ''}The remaining edits could not be confirmed as saved. They are still here; check the page message before retrying.`);
            return false;
        } finally { running.current = false; setSaving(false); }
    };
    return { saving, count: pending.length, message, failed, saveAll };
}
